import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Mic, Send, Volume2, VolumeX, X, Loader2, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const QUICK = ["Board exam me top kaise karun?", "Kaunse notes kharidne chahiye?", "Notes ka price range kya hai?"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: "Namaste! Main Voogly AI hoon. Notes, courses ya exam strategy — kuch bhi poochein." }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [speak, setSpeak] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" });
  }, [msgs, busy, open]);

  const playTts = async (t) => {
    try {
      const res = await api.get(`/ai/tts?text=${encodeURIComponent(t.slice(0, 600))}`, { responseType: "blob" });
      new Audio(URL.createObjectURL(res.data)).play();
    } catch { /* silent */ }
  };

  const send = async (content) => {
    const q = (content ?? text).trim();
    if (!q || busy) return;
    const next = [...msgs, { role: "user", content: q }];
    setMsgs(next);
    setText("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { messages: next.slice(-10).map((m) => ({ role: m.role, content: m.content })) });
      setMsgs([...next, { role: "assistant", content: data.reply }]);
      if (speak) playTts(data.reply);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleRecord = async () => {
    if (recording) { recorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 2000) { toast.error("Koi awaaz record nahi hui"); return; }
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const { data } = await api.post("/ai/transcribe", fd);
          if (data.text) await send(data.text);
          else toast.error("Samajh nahi aaya — dobara bolein");
        } catch (e) {
          toast.error(formatApiError(e));
        } finally {
          setBusy(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Mic permission chahiye");
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3">
      {open && (
        <div className="flex h-[480px] w-[350px] max-w-[92vw] flex-col overflow-hidden rounded-3xl border border-white/10 bg-ink shadow-2xl shadow-black/40" data-testid="ai-chat-panel">
          <div className="flex items-center justify-between border-b border-white/10 bg-ink2 px-4 py-3">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/15 text-amber-400"><Bot size={18} /></span>
              <div>
                <p className="font-display text-sm font-bold text-white">Voogly AI — Doubt Solver</p>
                <p className="text-[10px] text-slate-500">Voice bhi chalti hai — mic dabayein</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setSpeak(!speak)} aria-label="Toggle voice reply" data-testid="ai-chat-speak-toggle"
                className={`rounded-lg p-2 transition-colors ${speak ? "text-emerald-400" : "text-slate-500 hover:text-white"}`}>
                {speak ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
              <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="ai-chat-close-btn" className="rounded-lg p-2 text-slate-500 hover:text-white">
                <X size={16} />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="no-scrollbar flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${m.role === "user" ? "ml-auto bg-amber-500 text-ink font-medium" : "bg-ink2 text-slate-200 border border-white/5"}`} data-testid={m.role === "user" ? `ai-chat-msg-user-${i}` : `ai-chat-msg-bot-${i}`}>
                {m.content}
              </div>
            ))}
            {busy && (
              <div className="flex w-16 items-center justify-center rounded-2xl bg-ink2 py-3 border border-white/5">
                <Loader2 size={16} className="animate-spin text-amber-400" />
              </div>
            )}
            {msgs.length <= 1 && !busy && (
              <div className="flex flex-wrap gap-2 pt-2">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => send(q)} data-testid={`ai-chat-quick-${QUICK.indexOf(q)}`}
                    className="rounded-full border border-white/10 px-3 py-1.5 text-[11px] font-semibold text-slate-300 hover:border-amber-500/50 hover:text-amber-400">
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-white/10 p-3">
            <button onClick={toggleRecord} aria-label="Voice question" data-testid="ai-chat-mic-btn"
              className={`rounded-full p-2.5 transition-colors ${recording ? "bg-red-500 text-white animate-pulse" : "bg-white/5 text-slate-300 hover:text-amber-400"}`}>
              <Mic size={16} />
            </button>
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Apna doubt likhein…" data-testid="ai-chat-input"
              className="flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-500" />
            <button onClick={() => send()} disabled={busy || !text.trim()} aria-label="Send" data-testid="ai-chat-send-btn"
              className="rounded-full bg-amber-500 p-2.5 text-ink hover:bg-amber-400 disabled:opacity-50">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      <button onClick={() => setOpen(!open)} data-testid="ai-chat-launcher"
        className="flex items-center gap-2 rounded-full bg-amber-500 p-3.5 sm:py-3 sm:pl-4 sm:pr-5 font-display text-sm font-bold text-ink shadow-xl shadow-amber-500/25 hover:bg-amber-400 transition-colors">
        {open ? <X size={18} /> : <Bot size={18} className="shrink-0" />}
        {open ? (
          <span className="hidden sm:inline">Band karein</span>
        ) : (
          <span className="hidden sm:inline-flex items-center gap-1.5"><Sparkles size={14} /> AI Doubt Solver</span>
        )}
      </button>
    </div>
  );
}
